/**
 * Hash routing, without a router dependency.
 *
 * Three pages do not justify one, but shareable URLs do matter for an ops tool —
 * "look at this device" should be a link. The hash gives that with no server
 * configuration and no library, and it is trivially replaceable if real routing
 * is ever needed.
 */

export type RouteName = 'overview' | 'clients' | 'devices' | 'users' | 'audit' | 'accept';

export interface Route {
  name: RouteName;
  /** Set on a detail route, e.g. #/devices/abc123. */
  id: string | null;
}

export const DEFAULT_ROUTE: Route = { name: 'overview', id: null };

const NAMES: RouteName[] = ['overview', 'clients', 'devices', 'users', 'audit', 'accept'];

/**
 * Routes that do not require a session.
 *
 * `accept` is the only one: someone redeeming an invitation has no account yet,
 * which is the entire point. Enumerated here rather than checked inline, so
 * adding another way into the app while signed out is a deliberate edit.
 */
export const PUBLIC_ROUTES: RouteName[] = ['accept'];

export function isPublicRoute(name: RouteName): boolean {
  return PUBLIC_ROUTES.includes(name);
}

/**
 * Parses a location hash.
 *
 * Falls back to the default rather than erroring: a hash is user-editable and a
 * typo in the address bar should land somewhere sensible, not on a blank page.
 */
export function parseRoute(hash: string): Route {
  // Leading # and / stripped independently: a browser may hand back any of
  // '#/clients', '#clients', '/clients' or 'clients' depending on how it was set.
  const trimmed = hash.replace(/^#/, '').replace(/^\//, '').split('?')[0] ?? '';
  const [name, ...rest] = trimmed.split('/');

  if (!name || !NAMES.includes(name as RouteName)) return DEFAULT_ROUTE;

  /*
   * The remainder is rejoined rather than taking only the first segment. An
   * invite code is base64url and contains no slash today, but truncating an
   * identifier at the first one is the kind of assumption that breaks silently
   * the moment an encoding changes.
   */
  const id = rest.join('/');
  return { name: name as RouteName, id: id.length > 0 ? id : null };
}

export function routeHref(name: RouteName, id?: string): string {
  return id ? `#/${name}/${id}` : `#/${name}`;
}
